import React, { useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { useRutasManifiesto } from "../../../hooks/useManifest";
import { useVehiculoUbicacionActual } from "../../../hooks/gpsTracking/useVehicleTracking";

import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";

if (typeof window !== "undefined") {
  const workerUrl = "https://api.mapbox.com/mapbox-gl-js/v3.11.0/mapbox-gl-csp-worker.js";
  const blobCode = `importScripts('${workerUrl}');`;
  const workerBlob = new Blob([blobCode], { type: "application/javascript" });
  mapboxgl.workerUrl = URL.createObjectURL(workerBlob);
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const isValidCoordinate = (lat, lng) => {
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  return (
    Number.isFinite(parsedLat) &&
    Number.isFinite(parsedLng) &&
    parsedLat >= -90 &&
    parsedLat <= 90 &&
    parsedLng >= -180 &&
    parsedLng <= 180
  );
};

const normalizeCoordinate = (loc) => ({
  ...loc,
  loclat: Number(loc.loclat),
  loclng: Number(loc.loclng),
});

const formatStopTime = (value) => {
  if (!value) return "Hora no asignada";

  const raw = String(value).trim();

  // Caso: solo hora -> "08:30" o "08:30:00"
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.slice(0, 5);
  }

  // Caso: timestamp ISO o fecha completa
  const parsed = parseISO(raw);

  if (isValid(parsed)) {
    return format(parsed, "dd-MM-yyyy HH:mm", { locale: es });
  }

  // Fallback seguro
  return raw;
};

const formatVehicleTimestamp = (timestamp) => {
  if (!timestamp) return "Sin fecha de actualización";

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return String(timestamp);
  }

  return date.toLocaleString();
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const RouteMap = ({ route, vehid }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const vehicleMarkerRef = useRef(null);

  const routeId = !Array.isArray(route) ? route : null;

  const { paradas: paradasActuales = [] } = useRutasManifiesto(routeId);
  const { data: ubicacionActual } = useVehiculoUbicacionActual(
    vehid,
    routeId,
    Boolean(vehid)
  );

  const locations = useMemo(() => {
    const source = Array.isArray(route) ? route : paradasActuales;

    return [...(source || [])]
      .filter((loc) => isValidCoordinate(loc.loclat, loc.loclng))
      .map(normalizeCoordinate)
      .sort((a, b) => Number(a.parord || 0) - Number(b.parord || 0));
  }, [route, paradasActuales]);

  useEffect(() => {
    if (!mapContainer.current || locations.length < 2) return;

    if (!mapboxgl.accessToken) {
      console.error("Mapbox token no configurado.");
      return;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [locations[0].loclng, locations[0].loclat],
      zoom: 12,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.current.on("load", async () => {
      if (!map.current) return;

      const bounds = new mapboxgl.LngLatBounds();

      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      locations.forEach((loc, idx) => {
        const coord = [loc.loclng, loc.loclat];
        bounds.extend(coord);

        const stopType =
          idx === 0
            ? "ORIGEN"
            : idx === locations.length - 1
              ? "DESTINO"
              : "INTERMEDIA";

        const popupHtml = `
          <div style="font-family: Arial, sans-serif; min-width: 180px;">
            <strong>${idx + 1}. ${escapeHtml(loc.locnom || "Sin nombre")}</strong><br/>
            <span>${escapeHtml(stopType)}</span><br/>
            <span>Hora: ${escapeHtml(formatStopTime(loc.parhorest))}</span>
          </div>
        `;

        const marker = new mapboxgl.Marker({
          color:
            idx === 0
              ? "#2e7d32"
              : idx === locations.length - 1
                ? "#d32f2f"
                : "#1976d2",
        })
          .setLngLat(coord)
          .setPopup(new mapboxgl.Popup().setHTML(popupHtml))
          .addTo(map.current);

        markersRef.current.push(marker);
      });

      map.current.fitBounds(bounds, {
        padding: 70,
        maxZoom: 15,
      });

      try {
        const coordsString = locations
          .map((loc) => `${loc.loclng},${loc.loclat}`)
          .join(";");

        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsString}?geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`;

        const res = await fetch(url);
        const data = await res.json();

        if (!map.current || !data.routes?.[0]?.geometry) return;

        const routeGeo = {
          type: "Feature",
          geometry: data.routes[0].geometry,
        };

        if (map.current.getLayer("route-line")) {
          map.current.removeLayer("route-line");
        }

        if (map.current.getSource("route-line")) {
          map.current.removeSource("route-line");
        }

        map.current.addSource("route-line", {
          type: "geojson",
          lineMetrics: true,
          data: routeGeo,
        });

        map.current.addLayer({
          id: "route-line",
          type: "line",
          source: "route-line",
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-width": 6,
            "line-gradient": [
              "interpolate",
              ["linear"],
              ["line-progress"],
              0,
              "#2b5876",
              0.5,
              "#185a9d",
              1,
              "#2b5876",
            ],
          },
        });
      } catch (err) {
        console.error("Error al trazar la ruta:", err);
      }
    });

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      if (vehicleMarkerRef.current) {
        vehicleMarkerRef.current.remove();
        vehicleMarkerRef.current = null;
      }

      if (map.current) {
        map.current.remove();
        map.current = null;
      }

      if (mapContainer.current) {
        mapContainer.current.innerHTML = "";
      }
    };
  }, [locations]);

  useEffect(() => {
    if (!map.current || !ubicacionActual) return;

    const { lat, lng, timestamp } = ubicacionActual;

    if (!isValidCoordinate(lat, lng)) return;

    const popupHtml = `
      <div style="font-family: Arial, sans-serif; min-width: 180px;">
        <strong>Ubicación Actual</strong><br/>
        ${escapeHtml(formatVehicleTimestamp(timestamp))}
      </div>
    `;

    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.setLngLat([Number(lng), Number(lat)]);
      vehicleMarkerRef.current.setPopup(new mapboxgl.Popup().setHTML(popupHtml));
      return;
    }

    vehicleMarkerRef.current = new mapboxgl.Marker({ color: "red" })
      .setLngLat([Number(lng), Number(lat)])
      .setPopup(new mapboxgl.Popup().setHTML(popupHtml))
      .addTo(map.current);
  }, [ubicacionActual]);

  if (locations.length < 2) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          background: "#f8fafc",
          color: "#64748b",
          fontWeight: 600,
        }}
      >
        La ruta necesita al menos 2 paradas con coordenadas válidas.
      </div>
    );
  }

  return (
    <div
      ref={mapContainer}
      style={{
        width: "100%",
        height: "100%",
        minHeight: 320,
        borderRadius: 8,
        overflow: "hidden",
      }}
    />
  );
};

export default RouteMap;